import React, { FC, useEffect, useState } from 'react'

import { alpha, Typography, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import getSymbolFromCurrency from 'currency-symbol-map'
import { endOfMonth, format, isSameMonth, startOfMonth } from 'date-fns'
import { de, enUS, es, fr, it, uk } from 'date-fns/locale'
import Calendar from 'react-calendar'
import { useTranslation } from 'react-i18next'

import { showToast } from '../../../components/Toasts'
import { useAppSelector } from '../../../store/hooks'
import { useThemeMode } from '../../../theme/ThemeModeContext'
import { currencyFormatter } from '../../../utils'
import { DashboardService } from '../DashboardService'
import { CalendarCellDataType } from '../types'

import { CalendarCellWrapper } from './CalendarCellWrapper'
import { CalendarHeader } from './CalendarHeader'
import { getCalendarApiDayKey, getCalendarDayKey, getMonthAnchorDate } from './calendarUtils'
import { DailyStatsModal } from './DailyStatsModal'

const UK_SHORT_WEEKDAYS = ['Нд', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']

const CalendarComponent: FC = () => {
  const { spacing, palette } = useTheme()
  const { resolvedMode } = useThemeMode()
  const { t, i18n } = useTranslation()
  const language = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0]
  const dateLocale = { en: enUS, uk, es, fr, it, de }[language] || enUS

  const { currentAccount, period, lastAccountUpdate } = useAppSelector(
    ({ userAccounts }) => userAccounts,
  )
  const selectedPeriod = period[1] ? new Date(Date.parse(period[1])) : null

  const [currentDate, setCurrentDate] = useState(getMonthAnchorDate(selectedPeriod || new Date()))
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)

  const [selectedMonthData, setSelectedMonthData] = useState<CalendarCellDataType[]>([])
  const monthDataByDay = selectedMonthData.reduce<Record<string, CalendarCellDataType>>(
    (acc, day) => {
      acc[getCalendarApiDayKey(day.date)] = day
      return acc
    },
    {},
  )

  const cellColors = {
    light: {
      positive: alpha(palette.green[400] as string, 0.16),
      negative: alpha(palette.error.main, 0.3) as string,
    },
    dark: {
      positive: alpha(palette.green[400] as string, 0.8),
      negative: alpha(palette.red[400] as string, 0.8),
    },
  }

  const tileContent = ({ date }: { date: Date }) => {
    if (!isSameMonth(currentDate, date)) {
      return (
        <Box
          height={spacing(8)}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
        />
      )
    }
    const target = monthDataByDay[getCalendarDayKey(date)]
    if (target) {
      const { sum, count } = target
      const tradesCount = Number(count)
      const formattedMoneyValue = currencyFormatter(currentAccount?.currency || '', Number(sum))
      const isSumBig = Math.abs(sum) >= 1000
      const bigSum = `${sum < 0 ? '-' : ''}${getSymbolFromCurrency(
        currentAccount?.currency || 'GBP',
      )}${(Math.abs(sum) / 1000).toFixed(1)}K`

      const bgColor =
        sum > 0 ? cellColors[resolvedMode].positive : cellColors[resolvedMode].negative

      return (
        <CalendarCellWrapper date={date} bgColor={bgColor} setDateForModal={setSelectedDate}>
          <Typography
            fontWeight="bold"
            textAlign="start"
            fontSize={{ xs: 8, sm: 12 }}
            color={palette.text.primary}
          >
            {isSumBig ? bigSum : formattedMoneyValue}
          </Typography>
          <Typography
            color={palette.text.secondary}
            fontSize={{ xs: 7, sm: 10 }}
            fontWeight={500}
            textAlign="start"
          >
            {t('calendarTradesCount', { count: Number.isFinite(tradesCount) ? tradesCount : 0 })}
          </Typography>
        </CalendarCellWrapper>
      )
    } else {
      return <CalendarCellWrapper date={date} />
    }
  }

  const formatShortWeekday = (_: string | undefined, date: Date) => {
    if (language === 'uk') {
      return UK_SHORT_WEEKDAYS[date.getDay()]
    }

    const localizedWeekday = format(date, 'EEE', { locale: dateLocale }).replace('.', '')
    return localizedWeekday.charAt(0).toUpperCase() + localizedWeekday.slice(1)
  }

  useEffect(() => {
    if (period[1]) {
      setCurrentDate(getMonthAnchorDate(new Date(Date.parse(period[1]))))
    }
  }, [period])

  useEffect(() => {
    ;(async () => {
      if (currentAccount?.id && currentDate) {
        const date = new Date(currentDate)
        const start = startOfMonth(date).toISOString()
        const end = endOfMonth(date).toISOString()
        const period: [string | null, string | null] = [start, end]
        try {
          const { data } = await DashboardService.getCalendarData({
            account: currentAccount.id,
            period: period,
          })
          setSelectedMonthData(data)
        } catch (error) {
          showToast.error(error.message)
        }
      }
    })()
  }, [currentDate, currentAccount?.id, lastAccountUpdate])

  return (
    <>
      <Box
        className="dashboard-calendar"
        width="100%"
        padding={spacing(3)}
        display="flex"
        flexDirection="column"
        gap={spacing(1)}
        borderRadius={spacing(0.5)}
        sx={{
          '& .tile': {
            height: { xs: spacing(6), sm: spacing(8) },
            border: `0.5px solid ${palette.divider}`,
            backgroundColor: palette.background.paper,
            padding: 0,
            margin: 0,
            ml: 0,

            '& .MuiBox-root': {
              height: { xs: spacing(6), sm: spacing(8) },
            },
          },
          '& .react-calendar__month-view__days__day--neighboringMonth': {
            color: 'transparent',
          },
          '& .react-calendar__month-view__weekdays__weekday abbr': {
            textDecoration: 'none',
          },
          '& .react-calendar__month-view__weekdays__weekday': {
            fontFamily: 'Wix Madefor Text',
            color: palette.grey[700],
            fontWeight: 800,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: spacing(5),
          },
          '& .react-calendar__tile abbr': {
            display: 'none',
          },

          backgroundColor: palette.background.paper,
        }}
      >
        <CalendarHeader currentDate={currentDate} setCurrentDate={setCurrentDate} />
        <Calendar
          key={selectedDate?.toISOString()}
          value={currentDate}
          activeStartDate={currentDate}
          tileClassName="tile"
          tileContent={tileContent}
          calendarType="ISO 8601"
          showNavigation={false}
          formatShortWeekday={formatShortWeekday}
          className="period-filter-popup"
        />
      </Box>
      <DailyStatsModal selectedDate={selectedDate} setSelectedDate={setSelectedDate} />
    </>
  )
}

export const DashboardCalendar = React.memo(CalendarComponent)
